import { useEffect, useState } from 'react';

const TABS = ['Info', 'Game Limits', 'Help'];
const SPEEDS = {
  fast: { label: 'Fast', duration: 2000 },
  normal: { label: 'Normal', duration: 4000 },
  slow: { label: 'Slow', duration: 7000 },
};

const MULTIPLIERS = [
  { label: 'x2', color: '#E8C800', textColor: '#1a1000' },
  { label: 'x4', color: '#3498DB', textColor: '#fff' },
  { label: 'x5', color: '#E74C3C', textColor: '#fff' },
  { label: 'x10', color: '#2ECC71', textColor: '#fff' },
  { label: 'x20', color: '#E070CC', textColor: '#fff' },
  { label: 'x40', color: '#D8D8D8', textColor: '#1a1a1a' },
];

export function InfoDrawer({
  isOpen,
  onClose,
  selectedSpeed = 'normal',
  onSpeedChange,
  isSoundEnabled = true,
  onSoundChange,
  onShowTutorial,
}) {
  const [activeTab, setActiveTab] = useState('Info');
  const [isSpeedModalOpen, setIsSpeedModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('Info');
    }
  }, [isOpen]);

  function handleSpeedSelect(speed) {
    onSpeedChange?.(speed);
    setIsSpeedModalOpen(false);
  }

  if (!isOpen) return null;

  return (
    <div
      className="sw-drawer-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="sw-drawer"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Game Info"
      >
        {/* Drag handle */}
        <div className="sw-drawer-handle" />

        {/* Header */}
        <div className="sw-drawer-header">
          <span className="sw-drawer-title">Game Info</span>
          <button className="sw-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Tabs */}
        <div className="sw-drawer-tabs" role="tablist">
          {TABS.map(tab => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              className={`sw-drawer-tab${activeTab === tab ? ' sw-drawer-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="sw-drawer-content">
          {activeTab === 'Info' && (
            <>
              <div className='flex gap-2 items-center justify-around'>
                {/* Multipliers */}
                <section className="sw-drawer-section">
                  <h3 className="sw-drawer-section-title">Multipliers</h3>
                  <div className="sw-drawer-multipliers-grid">
                    {MULTIPLIERS.map(m => (
                      <div key={m.label} className="sw-multi-row">
                        <div
                          className="sw-multi-box"
                          style={{ background: m.color }}
                        />
                        <span className="sw-multi-text">{m.label}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="flex flex-col gap-4">
                  {/* Spin Speed */}
                  <section className="sw-drawer-section">
                    <h3 className="sw-drawer-section-title">Spin Speed</h3>
                    <div className="sw-drawer-speed-row">
                      <button
                        type="button"
                        className="sw-drawer-speed-btn"
                        onClick={() => setIsSpeedModalOpen(true)}
                        aria-expanded={isSpeedModalOpen}
                        aria-controls="sw-drawer-speed-modal"
                      >
                        <span className="sw-drawer-speed-btn__value">{SPEEDS[selectedSpeed]?.label ?? SPEEDS.normal.label}</span>
                      </button>
                    </div>
                  </section>

                  {/* Game Sound */}
                  <section className="sw-drawer-section">
                    <h3 className="sw-drawer-section-title">Game Sound</h3>
                    <div className="sw-drawer-speed-row">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff', fontWeight: 'bold', fontSize: '14px', background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.26) 0%, rgba(255, 255, 255, 0.06) 24%, rgba(255, 255, 255, 0) 38%), linear-gradient(180deg, #3e9eff 0%, #1765d4 46%, #0a388f 100%)', padding: '8px 16px', borderRadius: '12px', border: '2px solid #ffd54a', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                        <input
                          type="checkbox"
                          checked={isSoundEnabled}
                          onChange={(e) => onSoundChange?.(e.target.checked)}
                          style={{ width: '18px', height: '18px', accentColor: '#ffd54a', cursor: 'pointer' }}
                        />
                        {isSoundEnabled ? 'Sound On' : 'Sound Off'}
                      </label>
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}

          {activeTab === 'Game Limits' && (
            <section className="sw-drawer-section">
              <h3 className="sw-drawer-section-title">Bet Limits</h3>
              <table className="sw-drawer-limits-table">
                <thead>
                  <tr>
                    <th>Multiplier</th>
                    <th>Min Bet</th>
                    <th>Max Bet</th>
                  </tr>
                </thead>
                <tbody>
                  {MULTIPLIERS.map(m => (
                    <tr key={m.label}>
                      <td>
                        <span
                          className="sw-drawer-limits-badge"
                          style={{ background: m.color, color: m.textColor }}
                        >
                          {m.label}
                        </span>
                      </td>
                      <td>0.50</td>
                      <td>100.00</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {activeTab === 'Help' && (
            <section className="sw-drawer-section">
              <h3 className="sw-drawer-section-title">Need a walkthrough?</h3>
              <p style={{ color: '#fff', opacity: 0.85, marginBottom: '16px', lineHeight: 1.5 }}>
                Open the guided tutorial any time to revisit the main controls and betting flow.
              </p>
              <div className="show-Tutorial">
                <button
                  type="button"
                  className="sw-drawer-speed-btn"
                  onClick={() => onShowTutorial?.()}
                >
                  Show Tutorial
                </button>
              </div>
            </section>
          )}
        </div>

        {isSpeedModalOpen ? (
          <div
            id="sw-drawer-speed-modal"
            className="sw-drawer-modal-overlay"
            onClick={() => setIsSpeedModalOpen(false)}
          >
            <div
              className="sw-drawer-modal-box"
              role="dialog"
              aria-modal="true"
              aria-label="Choose spin speed"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="sw-drawer-modal-actions">
                {Object.entries(SPEEDS).map(([speedKey, speed]) => (
                  <button
                    key={speedKey}
                    type="button"
                    className={`sw-drawer-modal-button${selectedSpeed === speedKey ? ' is-active' : ''}`}
                    onClick={() => handleSpeedSelect(speedKey)}
                  >
                    {speed.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
